/**
 * useVoice - ElevenLabs voice-first hook
 * 
 * Handles:
 * - Speech-to-text (browser native + fallback)
 * - Text-to-speech via ElevenLabs
 * - Voice activity detection
 * - Kid-friendly voice personas
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// Extend Window interface for Speech Recognition
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

export type VoicePersona = 'coach' | 'buddy' | 'narrator';

interface VoiceConfig {
  elevenLabsApiKey?: string;
  voiceId?: string;
  persona?: VoicePersona;
  onTranscript?: (text: string) => void;
  onSpeakStart?: () => void;
  onSpeakEnd?: () => void;
  onError?: (error: string) => void;
}

// Kid-friendly voice IDs for ElevenLabs (these are public voice IDs)
const VOICE_PERSONAS: Record<VoicePersona, { voiceId: string; name: string; style: string }> = {
  coach: {
    voiceId: 'EXAVITQu4vr4xnSDxMaL', // Sarah - encouraging coach
    name: 'Coach',
    style: 'encouraging, energetic',
  },
  buddy: {
    voiceId: 'onwK4e9ZLuTAKqWW03F9', // Daniel - friendly peer
    name: 'Buddy',
    style: 'casual, supportive',
  },
  narrator: {
    voiceId: 'N2lVS1w4EtoT3dr4eOWO', // Callum - game narrator
    name: 'Narrator',
    style: 'epic, dramatic',
  },
};

// Gaming phrases mapped to kid-friendly voice responses
export const VOICE_LINES = {
  welcome: "Yo! Ready to level up your brain today?",
  questStart: "New quest unlocked! Let's do this!",
  correct: "Boom! Nailed it! Plus ten XP!",
  incorrect: "Oof, not quite. No worries, respawn and try again!",
  streak: "You're on fire! Streak bonus activated!",
  levelUp: "LEVEL UP! You just unlocked new challenges!",
  badgeEarned: "Achievement unlocked! New badge in your inventory!",
  timeWarning: "Heads up! One minute left on the clock!",
  questComplete: "Quest complete! Loot incoming!",
  encouragement: "You got this! Just focus on one step at a time.",
  hint: "Pro tip: Check out the power rule pattern here.",
};

interface UseVoiceReturn {
  isListening: boolean;
  isSpeaking: boolean;
  transcript: string;
  startListening: () => void;
  stopListening: () => void;
  speak: (text: string, priority?: boolean) => Promise<void>;
  speakLine: (lineKey: keyof typeof VOICE_LINES) => Promise<void>;
  setPersona: (persona: VoicePersona) => void;
  currentPersona: VoicePersona;
  isSupported: boolean;
}

export function useVoice(config: VoiceConfig = {}): UseVoiceReturn {
  const {
    elevenLabsApiKey = import.meta.env.VITE_ELEVENLABS_API_KEY,
    persona: initialPersona = 'coach',
    onTranscript,
    onSpeakStart,
    onSpeakEnd,
    onError,
  } = config;

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [currentPersona, setCurrentPersona] = useState<VoicePersona>(initialPersona);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);

  // Check browser support
  const isSupported = typeof window !== 'undefined' && 
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;
    
    const recognition = new SpeechRecognitionCtor();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      const currentText = finalTranscript || interimTranscript;
      setTranscript(currentText);

      if (finalTranscript && onTranscript) {
        onTranscript(finalTranscript.trim());
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error);
      if (onError) onError(`Voice input error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
    };
  }, [isSupported, onTranscript, onError]);

  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return;

    try {
      // Stop speaking when user starts talking
      if (isSpeaking) {
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        setIsSpeaking(false);
      }

      recognitionRef.current.start();
      setIsListening(true);
      setTranscript('');
    } catch (err) {
      console.error('Failed to start listening:', err);
    }
  }, [isListening, isSpeaking]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;
    recognitionRef.current.stop();
    setIsListening(false);
  }, []);

  // Play audio from queue
  const playNextInQueue = useCallback(async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;

    isPlayingRef.current = true;
    const audioData = audioQueueRef.current.shift();

    if (!audioData || !audioContextRef.current) {
      isPlayingRef.current = false;
      return;
    }

    try {
      const audioBuffer = await audioContextRef.current.decodeAudioData(audioData);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);

      source.onended = () => {
        isPlayingRef.current = false;
        if (audioQueueRef.current.length > 0) {
          playNextInQueue();
        } else {
          setIsSpeaking(false);
          onSpeakEnd?.();
        }
      };

      source.start();
    } catch (err) {
      console.error('Audio playback error:', err);
      isPlayingRef.current = false;
      playNextInQueue();
    }
  }, [onSpeakEnd]);

  const speak = useCallback(async (text: string, priority = false) => {
    if (!elevenLabsApiKey) {
      // Fallback to browser TTS
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.1;
      utterance.pitch = 1.0;
      
      utterance.onstart = () => {
        setIsSpeaking(true);
        onSpeakStart?.();
      };
      
      utterance.onend = () => {
        setIsSpeaking(false);
        onSpeakEnd?.();
      };

      window.speechSynthesis.speak(utterance);
      return;
    }

    // Clear queue if priority message
    if (priority) {
      audioQueueRef.current = [];
    }

    try {
      setIsSpeaking(true);
      onSpeakStart?.();

      const voiceConfig = VOICE_PERSONAS[currentPersona];

      const response = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${voiceConfig.voiceId}/stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xi-api-key': elevenLabsApiKey,
          },
          body: JSON.stringify({
            text,
            model_id: 'eleven_turbo_v2', // Fast, good quality
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.75,
              style: 0.3, // Some expressiveness
              use_speaker_boost: true,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      const audioData = await response.arrayBuffer();
      audioQueueRef.current.push(audioData);
      playNextInQueue();
    } catch (err) {
      console.error('TTS error:', err);
      if (onError) onError('Voice output failed');
      setIsSpeaking(false);
      
      // Fallback to browser TTS
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    }
  }, [elevenLabsApiKey, currentPersona, onSpeakStart, onSpeakEnd, onError, playNextInQueue]);

  const speakLine = useCallback(async (lineKey: keyof typeof VOICE_LINES) => {
    const text = VOICE_LINES[lineKey];
    await speak(text, lineKey === 'levelUp' || lineKey === 'badgeEarned');
  }, [speak]);

  const setPersona = useCallback((persona: VoicePersona) => {
    setCurrentPersona(persona);
  }, []);

  return {
    isListening,
    isSpeaking,
    transcript,
    startListening,
    stopListening,
    speak,
    speakLine,
    setPersona,
    currentPersona,
    isSupported,
  };
}
